import { fetchUserInfoUsecase, logoutUsecase } from "@/init/initializeInstances";
import { isStoreMockMode, isStorePopupMode, STORE_PATHS } from "@/store/store-config";
import { clearUserRoleCookie } from "@/utils/cookie-utils";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import styles from "./store-header.module.css";

import { publicUrl } from "@/utils/public-url";
interface HeaderUser {
  displayName: string;
  hospitalName: string;
}

export default function StoreHeader() {
  const navigate = useNavigate();
  const location = useLocation();

  //MARK:- 사용자 표시 정보 (목 모드: 고정값 / 실 모드: 기존 사용자 정보 API)
  const [user, setUser] = useState<HeaderUser | null>(null);
  useEffect(() => {
    const load = async () => {
      if (isStoreMockMode()) {
        setUser({ displayName: "테스트 의료진", hospitalName: "서울밝은눈안과" });
        return;
      }
      const result = await fetchUserInfoUsecase.execute();
      if (result.isSuccess) {
        setUser({
          displayName: result.value.fullName,
          hospitalName: result.value.hospitalInfo?.name ?? "",
        });
      }
    };
    load();
  }, []);

  //MARK:- 마이페이지 드롭다운
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // 라우트 변경 시 메뉴 닫기
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // 바깥 클릭/ESC 로 닫기
  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }
    const handlePointerDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMenuOpen]);

  const handleLogout = async () => {
    setIsMenuOpen(false);
    await logoutUsecase.execute();
    clearUserRoleCookie();
    navigate("/", { replace: true });
  };

  return (
    <header className={styles.header}>
      <div className={styles.inner}>
        <Link to={STORE_PATHS.ROOT} className={styles.brand}>
          <img src={publicUrl("/images/logoSalpha.png")} alt="에스알파테라퓨틱스 로고" className={styles.logo} />
          <span className={styles.title}>MyoHabit™ 이용권 스토어</span>
        </Link>

        <nav className={styles.nav}>
          <div className={styles.menuContainer} ref={menuRef}>
            <button
              type="button"
              className={styles.menuButton}
              onClick={() => setIsMenuOpen((prev) => !prev)}
              aria-expanded={isMenuOpen}
              aria-haspopup="menu"
            >
              <span className={styles.menuButtonAvatar} aria-hidden="true">
                {(user?.displayName ?? "?").slice(0, 1)}
              </span>
              <span className={styles.menuButtonLabel}>{user?.displayName ?? "마이페이지"}</span>
              <span
                className={`${styles.menuButtonChevron} ${isMenuOpen ? styles.menuButtonChevronOpen : ""}`}
                aria-hidden="true"
              >
                ▾
              </span>
            </button>

            {isMenuOpen && (
              <div className={styles.menuDropdown} role="menu">
                {user && (
                  <>
                    <div className={styles.menuUserInfo}>
                      <span className={styles.menuUserName}>{user.displayName}</span>
                      <span className={styles.menuUserHospital}>{user.hospitalName}</span>
                    </div>
                    <div className={styles.menuDivider} />
                  </>
                )}
                <Link to={STORE_PATHS.PAYMENTS} className={styles.menuItem} role="menuitem">
                  결제 내역
                </Link>
                <div className={styles.menuDivider} />
                {!isStorePopupMode() && (
                  <Link to="/home" className={styles.menuItem} role="menuitem">
                    의료진용 웹으로
                  </Link>
                )}
                <button
                  type="button"
                  className={`${styles.menuItem} ${styles.menuItemDanger}`}
                  onClick={handleLogout}
                  role="menuitem"
                >
                  로그아웃
                </button>
              </div>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
}
